import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { authAPI } from '../api';
import { updateCurrencyRates } from '../utils/currency';

const AuthContext = createContext(null);
const USER_REFRESH_TTL_MS = 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('nb_user')); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [publicSettings, setPublicSettings] = useState(null);
  const lastRefreshAtRef = useRef(user ? Date.now() : 0);
  const refreshPromiseRef = useRef(null);

  const applyUser = useCallback((nextUser) => {
    setUser(nextUser);
    lastRefreshAtRef.current = Date.now();
    if (nextUser) {
      localStorage.setItem('nb_user', JSON.stringify(nextUser));
    } else {
      localStorage.removeItem('nb_user');
    }
  }, []);

  useEffect(() => {
    // Always fetch public settings to sync currency rates
    authAPI.publicSettings().then(res => {
      if (res.data?.settings) {
        setPublicSettings(res.data.settings);
        if (res.data.settings.currency_rates) {
          updateCurrencyRates(res.data.settings.currency_rates);
        }
      }
    }).catch(console.error);

    const token = localStorage.getItem('nb_token');
    if (token) {
      authAPI.me()
        .then(r => { applyUser(r.data.user); })
        .catch(() => { localStorage.removeItem('nb_token'); applyUser(null); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [applyUser]);

  const login = useCallback(async (email, password) => {
    const res = await authAPI.login({ email, password });
    localStorage.setItem('nb_token', res.data.token);
    applyUser(res.data.user);
    return res.data.user;
  }, [applyUser]);

  const register = useCallback(async (data) => {
    const res = await authAPI.register(data);
    localStorage.setItem('nb_token', res.data.token);
    applyUser(res.data.user);
    return res.data.user;
  }, [applyUser]);

  const loginWithGoogle = useCallback(async (idToken, referralCode = null) => {
    const res = await authAPI.google({ idToken, referral_code: referralCode });
    localStorage.setItem('nb_token', res.data.token);
    applyUser(res.data.user);
    return res.data.user;
  }, [applyUser]);

  const logout = useCallback(() => {
    localStorage.removeItem('nb_token');
    applyUser(null);
  }, [applyUser]);

  const refreshUser = useCallback(async ({ force = false } = {}) => {
    const token = localStorage.getItem('nb_token');
    if (!token) {
      applyUser(null);
      return null;
    }

    if (!force && refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    if (!force && user && Date.now() - lastRefreshAtRef.current < USER_REFRESH_TTL_MS) {
      return user;
    }

    refreshPromiseRef.current = authAPI.me()
      .then((res) => {
        applyUser(res.data.user);
        return res.data.user;
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          localStorage.removeItem('nb_token');
          applyUser(null);
        }
        throw err;
      })
      .finally(() => {
        refreshPromiseRef.current = null;
      });

    return refreshPromiseRef.current;
  }, [applyUser, user]);

  const value = useMemo(() => ({
    user,
    loading,
    publicSettings,
    login,
    register,
    loginWithGoogle,
    logout,
    refreshUser,
  }), [user, loading, publicSettings, login, register, loginWithGoogle, logout, refreshUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
